import { Component, computed, OnInit, Signal } from '@angular/core';
import { ContentComponent } from '../../components/content/content.component';
import { BreadcrumbsComponent } from '../../common/breadcrumbs/breadcrumbs.component';
import { ReactiveFormsModule } from '@angular/forms';
import { PaymentForm } from './payment.form';
import { Store } from '@ngrx/store';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { PaymentCommonService } from '../../services/payment.common.service';
import { ValidationErrorsComponent } from '../../common/validation-errors/validation-errors.component';
import { CommonModule } from '@angular/common';
import { LabelComponent } from '../../common/label/label.component';
import { AlertComponent } from '../../common/alert/alert.component';
import { environment } from '../../../environments/environment';
import { SessionService } from '../../services/ssession.service';
import { UserDetails } from '../../store/reducers/user-details.reducer';
import { selectUser } from '../../store/selectors/user-details.selectors';
import { loadUserDetails } from '../../store/actions/user-details.actions';

/**
 * Tytuły płatności (Fee), pod którymi pojawia się dodatkowy alert.
 * Dopasowanie działa po ID (UUID) lub po nazwie tytułu — ID jest odporne
 * na późniejszą zmianę nazwy w panelu administracyjnym, więc jest zalecane.
 */
const PAYMENT_POSITION_ALERT_KEYS: string[] = [
  'feb1f349-5e3c-47d8-862d-06c6a7c40e0f',
  'ef5f4606-76ad-4eeb-b1e7-fa039caa1b18'
];

@Component({
  selector: 'app-payments-page',
  standalone: true,
  imports: [
    CommonModule,
    ContentComponent,
    BreadcrumbsComponent,
    ReactiveFormsModule,
    ValidationErrorsComponent,
    LabelComponent,
    RouterModule,
    AlertComponent
  ],
  providers: [PaymentForm],
  templateUrl: './payments-page.component.html'
})
export class PaymentsPageComponent implements OnInit {

  public paymentToken: string | undefined = this.route.snapshot.queryParams['payum_token'] || this.route.snapshot.queryParams['amp;payum_token'];

  public appId: string | undefined = this.route.snapshot.queryParams['appId'];

  public serviceType: string | undefined = this.route.snapshot.queryParams['serviceType'] || this.route.snapshot.queryParams['amp;serviceType'];

  public authed: boolean = SessionService.Authed();

  public user: Signal<UserDetails | null> = this.store$.selectSignal(selectUser);

  public showPositionAlert: Signal<boolean> = computed(() => {
    const id: string = this.form.selectedPositionId();
    if (!id) {
      return false;
    }
    const position = this.form.paymentPositions().filter(p => p?.id === id)[0];
    return !!position && PAYMENT_POSITION_ALERT_KEYS
      .map(key => key.trim())
      .filter(key => !!key)
      .some(key => key === position.id || key === position.name?.trim());
  });

  constructor(
    private store$: Store,
    private route: ActivatedRoute,
    private router: Router,
    private paymentCommonService: PaymentCommonService,
    public form: PaymentForm
  ) {
  }

  public onPastDataFromPortal(): void {
    this.form?.readApplicantDataFromPortal(this.user());
  }

  public onPastDataFromLoginGov(): void {
    this.form?.readApplicantDataFromGovPl(SessionService.GetTokenData());
  }

  public submit(): void {
    if (this.form.valid) {
      this.paymentCommonService.goToPay(this.form.toWriteModel())
        .toPromise().then();
    } else {
      this.form.markAllAsTouched();
    }
  }

  public ngOnInit(): void {
    if (this.route.snapshot.queryParams['token']) {
      SessionService.SetAccessToken(this.route.snapshot.queryParams['token']);
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { serviceType: this.serviceType }
      }).finally(() => location.reload());
    }

    if (this.authed) {
      this.store$.dispatch(loadUserDetails());
    }
    switch (true) {
      case !!this.paymentToken && !!this.appId && !!this.serviceType: {
        this.router.navigateByUrl('/platnosci-online/' + this.paymentToken + '/' + this.appId + this.serviceType);
        break;
      }
      case !!this.paymentToken: {
        this.router.navigateByUrl('/platnosci-online/' + this.paymentToken);
        break;
      }
      case !!this.serviceType: {
        this.form.paymentCategories$.subscribe(c => {
          this.form.get('paymentCategory')?.setValue(this.serviceType);
          this.form.paymentCategoryChange(this.serviceType || '');
        });
        break;
      }
      default: {
        // do nothing
      }
    }
  }
}
